/**
 * 图层栈搭建(方案 A1)+ 1920×1080 逻辑分辨率缩放 + 后期装配 + 引擎级每帧更新。
 * 世界实体(岛/烛民/效果)在逻辑坐标下工作,由本 Stage 统一缩放居中。
 */
import { Application, Container, Sprite, Texture } from 'pixi.js';
import { AdvancedBloomFilter } from 'pixi-filters';
import { sky, SKY_STOPS } from './SkyRenderer';
import { world as worldClock } from './Ticker';
import { toHexStr } from './Palette';
import { ParticlePool, setPools } from './ParticlePool';
import { Vignette, Grain, Flash, Shake, ColorGrade } from '../post/Post';

export const LW = 1920;
export const LH = 1080;
export const GROUND_Y = LH * 0.62; // 岛面海拔线(逻辑 y)

export interface Layers {
  celestial: Container;
  farClouds: Container;
  auroraLayer: Container;
  islandFar: Container;
  islandMid: Container;
  cloudSeaBack: Container;
  mainIsland: Container;
  cloudSeaFront: Container;
  weatherFX: Container;
  eventFX: Container;
  mistLayer: Container;
}

export class Stage {
  app!: Application;
  gradedGroup = new Container();
  world = new Container();      // 逻辑坐标,缩放居中,承 layers 1-11 + 震屏
  layers!: Layers;

  // 后期
  flash = new Flash();
  vignette = new Vignette();
  grain = new Grain();
  shake = new Shake();
  colorGrade = new ColorGrade();

  /**
   * 辉光(§3.2「霓虹级 bloom」)。
   * 注:此项在《灵墟》§F 的后期四件套里被漏掉了,而 §3.2 明确要求——
   * 缺它则 ember/divine 亮核只是"贴了张渐变图的橙点",本作发光美学的根基不成立。
   * 参数是在 dev.html 里对着画面实调出来的,不是拍脑袋:
   *  - threshold 0.88:必须卡在"只有亮核(ember/divine/moon)过阈、天空不过阈"。
   *    低于 0.85 时长暮天光本身开始过阈,全屏泛白糊掉。
   *  - bloomScale 0.62:亮核本身已是多层 add 叠加(远超 1.0),再给高 scale 会把
   *    火种/太阳烧成一个死白圆球,细节(窗灯、烛民)全糊成一片。
   */
  bloom = new AdvancedBloomFilter({
    threshold: 0.88, bloomScale: 0.62, brightness: 1.0, blur: 10, quality: 6,
  });

  // 天空底(layer 0,全画布,不缩放)
  private skyBg = new Sprite();
  private skyCanvas = document.createElement('canvas');
  private skyTex!: Texture;

  eventPool!: ParticlePool;
  weatherPool!: ParticlePool;

  private w = 0; private h = 0; private scale = 1; private offX = 0; private offY = 0;

  init(app: Application) {
    this.app = app;

    // 天空底
    this.skyCanvas.width = 8; this.skyCanvas.height = 512;
    this.skyTex = Texture.from(this.skyCanvas);
    this.skyBg.texture = this.skyTex;

    // layers 1-11
    const mk = () => new Container();
    this.layers = {
      celestial: mk(), farClouds: mk(), auroraLayer: mk(), islandFar: mk(), islandMid: mk(),
      cloudSeaBack: mk(), mainIsland: mk(), cloudSeaFront: mk(), weatherFX: mk(), eventFX: mk(), mistLayer: mk(),
    };
    this.world.addChild(
      this.layers.celestial, this.layers.farClouds, this.layers.auroraLayer,
      this.layers.islandFar, this.layers.islandMid, this.layers.cloudSeaBack,
      this.layers.mainIsland, this.layers.cloudSeaFront, this.layers.weatherFX,
      this.layers.eventFX, this.layers.mistLayer,
    );
    this.layers.auroraLayer.visible = false;

    // 调色作用于 天空底 + 世界;闪白/暗角/噪点在其上不受调色
    this.gradedGroup.addChild(this.skyBg, this.world);
    // 顺序:先 bloom(让亮核溢出)再调色(统一情绪),闪白/暗角/噪点在滤镜之外
    this.gradedGroup.filters = [this.bloom, this.colorGrade.filter];
    this.colorGrade.target = this.gradedGroup;
    app.stage.addChild(this.gradedGroup, this.flash.sprite, this.vignette.sprite, this.grain.sprite);

    // 粒子池
    this.eventPool = new ParticlePool(this.layers.eventFX, 900);
    this.weatherPool = new ParticlePool(this.layers.weatherFX, 1200);
    setPools(this.eventPool, this.weatherPool);
  }

  resize(w: number, h: number) {
    this.w = w; this.h = h;
    this.scale = Math.min(w / LW, h / LH);
    this.offX = (w - LW * this.scale) / 2;
    this.offY = (h - LH * this.scale) / 2;
    this.world.scale.set(this.scale);
    this.world.position.set(this.offX, this.offY);
    this.skyBg.width = w; this.skyBg.height = h;
    this.flash.resize(w, h); this.vignette.resize(w, h); this.grain.resize(w, h);
  }

  private drawSky() {
    const s = sky.state;
    const ctx = this.skyCanvas.getContext('2d')!;
    // 停靠位置见 SKY_STOPS 注释:最亮的 bottom 落在地平线上(而非画面最底部),
    // 否则天空最亮的一段全被云海与倒锥遮住,地平线附近一片近黑,剪影无从"减"起。
    const g = ctx.createLinearGradient(0, 0, 0, 512);
    g.addColorStop(SKY_STOPS.top, toHexStr(s.top));
    g.addColorStop(SKY_STOPS.mid, toHexStr(s.mid));
    g.addColorStop(SKY_STOPS.bottom, toHexStr(s.bottom));
    g.addColorStop(1, toHexStr(s.bottom));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 8, 512);
    this.skyTex.source.update();
  }

  /** 引擎级每帧更新(世界实体的 update 由 World 编排另行调用) */
  update(dt: number) {
    sky.update(dt);
    worldClock.updateWind(dt);
    this.drawSky();
    this.colorGrade.update();
    this.grain.update();
    this.flash.update(dt);
    this.shake.update(dt);
    this.world.position.set(this.offX + this.shake.ox, this.offY + this.shake.oy);
    this.eventPool.update(dt);
    this.weatherPool.update(dt);
  }

  /** 逻辑 → 无关;供效果查询屏幕量 */
  get logicalW() { return LW; }
  get logicalH() { return LH; }
  get groundY() { return GROUND_Y; }
}
